import { useState } from 'react';
import { Select, Space, Modal, Descriptions, Typography } from 'antd';
import { PageHeader } from '../../components/common/PageHeader';
import { DataTable } from '../../components/common/DataTable';
import { useAuditLogs, useAuditLogDetail } from '../../hooks/useSystem';
import dayjs from 'dayjs';
import type { AdminAuditLogItem } from '../../types/system';
import type { ColumnsType } from 'antd/es/table';

export function AuditLogsPage() {
  const [params, setParams] = useState({ page: 1, per_page: 50, action: undefined as string | undefined, target_type: undefined as string | undefined });
  const { data, isLoading } = useAuditLogs(params);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: detail } = useAuditLogDetail(selectedId || '');

  const columns: ColumnsType<AdminAuditLogItem> = [
    { title: 'Admin', dataIndex: 'admin_email', key: 'admin' },
    { title: 'Role', dataIndex: 'admin_role', key: 'role' },
    { title: 'Action', dataIndex: 'action', key: 'action' },
    { title: 'Target', dataIndex: 'target_type', key: 'target' },
    { title: 'Identifier', dataIndex: 'target_identifier', key: 'identifier', render: (v: string | null) => v || '-' },
    { title: 'IP', dataIndex: 'ip_address', key: 'ip', render: (v: string | null) => v || '-' },
    { title: 'Time', dataIndex: 'created_at', key: 'time', render: (v: string) => dayjs(v).format('MMM D HH:mm:ss') },
  ];

  return (
    <>
      <PageHeader title="Audit Logs" />
      <DataTable<AdminAuditLogItem>
        columns={columns} dataSource={data?.data} loading={isLoading} total={data?.total} page={params.page} perPage={params.per_page}
        onPageChange={(p, ps) => setParams((s) => ({ ...s, page: p, per_page: ps }))}
        onRow={(record) => ({ onClick: () => setSelectedId(record.id), style: { cursor: 'pointer' } })}
        extra={
          <Space>
            <Select placeholder="Action" allowClear style={{ width: 180 }} onChange={(v) => setParams((s) => ({ ...s, action: v, page: 1 }))} options={['admin.login', 'user.update', 'user.deactivate', 'subscription.refund', 'ticket.reply'].map((a) => ({ label: a, value: a }))} />
            <Select placeholder="Target" allowClear style={{ width: 140 }} onChange={(v) => setParams((s) => ({ ...s, target_type: v, page: 1 }))} options={['user', 'organization', 'subscription', 'invoice', 'support_ticket', 'feature_flag'].map((t) => ({ label: t, value: t }))} />
          </Space>
        }
      />
      <Modal title="Audit Log Detail" open={!!selectedId} onCancel={() => setSelectedId(null)} footer={null} width={640}>
        {detail && (
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Admin">{detail.admin_email} ({detail.admin_role})</Descriptions.Item>
            <Descriptions.Item label="Action">{detail.action}</Descriptions.Item>
            <Descriptions.Item label="Target">{detail.target_type} / {detail.target_identifier || detail.target_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="IP">{detail.ip_address || '-'}</Descriptions.Item>
            <Descriptions.Item label="Method">{detail.request_method} {detail.request_path}</Descriptions.Item>
            <Descriptions.Item label="Time">{dayjs(detail.created_at).format('MMM D, YYYY HH:mm:ss')}</Descriptions.Item>
            {detail.changes && <Descriptions.Item label="Changes"><Typography.Text><pre style={{ fontSize: 11, maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(detail.changes, null, 2)}</pre></Typography.Text></Descriptions.Item>}
          </Descriptions>
        )}
      </Modal>
    </>
  );
}
